import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { cardSubtle } from "@/lib/card-styles";
import {
  getDataPointHistory,
  getEquipmentKBDefinition,
  listDataPointSources,
  updateEquipment,
  uploadEquipmentDataPreview,
  confirmEquipmentDataUpload,
  type DataPointSample,
  type DataPointSource,
  type KBComponentDef,
  type KBDataPointDef,
  type UploadPreviewOut,
} from "@/lib/api/building";
import { UnmatchedColumnRemap } from "@/components/building/datapoints/UnmatchedColumnRemap";
import { formatSignalValue } from "@/lib/format-signal-value";
import {
  equipmentCandidateGroups,
  type RemapCandidateGroup,
} from "@/lib/kb-remap-groups";

interface Props {
  buildingId: string;
  equipmentId: string;
  operator: boolean;
  initial_declared: string[];
  on_declared_changed: (ids: string[]) => void;
  on_close: () => void;
}

const CRITICALITY_LABEL: Record<string, string> = {
  required: "Pakollinen",
  recommended: "Suositeltava",
  optional: "Valinnainen",
};

const CRITICALITY_CLASS: Record<string, string> = {
  required: "bg-red-500/10 text-red-400",
  recommended: "bg-warning-subtle text-warning-subtle-foreground",
  optional: "bg-muted text-muted-foreground",
};

const KIND_ORDER: Record<string, number> = {
  status: 0,
  sensor: 1,
  actuator: 2,
  setpoint: 3,
  calculated: 4,
};

const KIND_LABEL: Record<string, string> = {
  status: "Tila",
  sensor: "Mittaus",
  actuator: "Toimilaite",
  setpoint: "Asetusarvo",
  calculated: "Laskettu",
};

const KIND_CLASS: Record<string, string> = {
  status: "bg-sky-500/10 text-sky-400",
  sensor: "bg-blue-500/10 text-blue-400",
  actuator: "bg-orange-500/10 text-orange-400",
  setpoint: "bg-violet-500/10 text-violet-400",
  calculated: "bg-teal-500/10 text-teal-400",
};

type UploadPhase =
  "idle" | "previewing" | "preview_ready" | "confirming" | "done" | "error";

// A name from data_point_history is "canonical" if and only if it appears in the
// KB definition for this equipment. BMS signal names, behavior signals, and
// reliability_score are all excluded — they are not KB data_point_ids.
function canonical_ids_present(
  sources: DataPointSource[],
  equipment_id: string,
  kb_dp_ids: Set<string>,
): Set<string> {
  const eq = sources.find((s) => s.source_id === equipment_id);
  return new Set(
    (eq?.available_names ?? [])
      .map((n) => n.name)
      .filter((n) => kb_dp_ids.has(n)),
  );
}

// Returns true when the equipment has sensor readings in history but none of
// them are KB canonical data_point_ids — i.e., data was imported before the
// vendor-independence refactor and uses raw BMS signal names.
function has_only_legacy_names(
  sources: DataPointSource[],
  equipment_id: string,
  kb_dp_ids: Set<string>,
): boolean {
  const eq = sources.find((s) => s.source_id === equipment_id);
  if (!eq || eq.available_names.length === 0) return false;
  const sensor_names = eq.available_names
    .map((n) => n.name)
    .filter(
      (n) =>
        !n.startsWith("behavior_") &&
        !n.startsWith("kb_behavior_") &&
        n !== "reliability_score",
    );
  return sensor_names.length > 0 && !sensor_names.some((n) => kb_dp_ids.has(n));
}

function format_sample(
  s: DataPointSample,
  unit: string | null | undefined,
): string {
  return formatSignalValue(s, unit);
}

// ── Row component ──────────────────────────────────────────────────────────────

function DataPointRow({
  component_id,
  dp,
  present,
  declared,
  operator,
  on_toggle,
  last_sample,
}: {
  component_id: string;
  dp: KBDataPointDef;
  present: boolean;
  declared: boolean;
  operator: boolean;
  on_toggle: (id: string) => void;
  last_sample: DataPointSample | undefined;
}) {
  return (
    <tr className={cn("border-b border-border/20", !declared && "opacity-40")}>
      <td className="py-2 pr-3">
        <span className="text-xs font-medium">{dp.name_fi}</span>
        {dp.unit && (
          <span className="ml-1 text-[10px] text-muted-foreground">
            {dp.unit}
          </span>
        )}
      </td>
      <td className="py-2 pr-3">
        <span
          className={cn(
            "rounded px-1.5 py-0.5 text-[10px] font-medium",
            CRITICALITY_CLASS[dp.criticality] ??
              "bg-muted text-muted-foreground",
          )}
        >
          {CRITICALITY_LABEL[dp.criticality] ?? dp.criticality}
        </span>
      </td>
      <td className="py-2 pr-3">
        {dp.kind && (
          <span
            className={cn(
              "rounded px-1.5 py-0.5 text-[10px] font-medium",
              KIND_CLASS[dp.kind] ?? "bg-muted text-muted-foreground",
            )}
          >
            {KIND_LABEL[dp.kind] ?? dp.kind}
          </span>
        )}
      </td>
      <td className="py-2 pr-3 font-mono text-[10px] text-muted-foreground/60">
        {component_id}.{dp.id}
      </td>
      <td className="py-2 pr-3">
        {last_sample ? (
          <span className="text-xs font-medium tabular-nums">
            {format_sample(last_sample, dp.unit)}
          </span>
        ) : present ? (
          <span className="text-[10px] text-muted-foreground/60">✓ Tuotu</span>
        ) : (
          <span className="text-[10px] text-muted-foreground/40">
            — Ei dataa
          </span>
        )}
      </td>
      <td className="py-2 pl-2">
        <input
          type="checkbox"
          checked={declared}
          disabled={!operator}
          onChange={() => on_toggle(dp.id)}
          className="h-3.5 w-3.5 rounded accent-primary"
          title={declared ? "Poista aktivointi" : "Aktivoi signaali"}
        />
      </td>
    </tr>
  );
}

// ── Upload preview modal ───────────────────────────────────────────────────────

function UploadPreviewModal({
  preview,
  confirming,
  error,
  candidate_groups,
  remap_assignments,
  on_assign_unmatched,
  on_confirm,
  on_cancel,
}: {
  preview: UploadPreviewOut;
  confirming: boolean;
  error: string | null;
  /** Null while the KB catalog is loading/unavailable — remap degrades to read-only. */
  candidate_groups: RemapCandidateGroup[] | null;
  remap_assignments: Readonly<Record<string, string>>;
  on_assign_unmatched: (
    csv_column: string,
    canonical_id: string | null,
  ) => void;
  on_confirm: () => void;
  on_cancel: () => void;
}) {
  // Every matched column is always included (no per-row selection in this panel,
  // unlike the Syötä dataa dialog), so all of it — plus remapped picks — is claimed.
  const claimed_canonical_ids = new Set<string>([
    ...preview.matched.map((m) => m.canonical_id),
    ...Object.values(remap_assignments),
  ]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className={cn(cardSubtle, "w-full max-w-lg p-5 space-y-4")}>
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold">Vahvista CSV-tuonti</p>
          <span className="text-[10px] text-muted-foreground">
            {preview.row_count} riviä
          </span>
        </div>

        {preview.matched.length > 0 && (
          <div>
            <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              Tunnistetut signaalit ({preview.matched.length})
            </p>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border/30">
                  <th className="pb-1 pr-3 text-left font-medium text-muted-foreground">
                    CSV-sarake
                  </th>
                  <th className="pb-1 pr-3 text-left font-medium text-muted-foreground">
                    KB-signaali
                  </th>
                  <th className="pb-1 text-left font-medium text-muted-foreground">
                    Varmuus
                  </th>
                </tr>
              </thead>
              <tbody>
                {preview.matched.map((m) => (
                  <tr key={m.csv_column} className="border-b border-border/10">
                    <td className="py-1 pr-3 font-mono">{m.csv_column}</td>
                    <td className="py-1 pr-3">{m.name_fi}</td>
                    <td className="py-1 text-muted-foreground">
                      {Math.round(m.confidence * 100)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <UnmatchedColumnRemap
          unmatchedColumns={preview.unmatched}
          candidateGroups={candidate_groups}
          claimedIds={claimed_canonical_ids}
          assignments={remap_assignments}
          onAssign={on_assign_unmatched}
        />

        {preview.matched.length === 0 && (
          <p className="text-xs text-destructive">
            Tiedostosta ei löydetty tunnistettavia signaaleja. Tarkista
            CSV-sarakkeiden nimet.
          </p>
        )}

        {error && <p className="text-xs text-destructive">{error}</p>}

        <div className="flex justify-end gap-2 pt-1">
          <button
            onClick={on_cancel}
            disabled={confirming}
            className="rounded px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            Peruuta
          </button>
          <button
            onClick={on_confirm}
            disabled={confirming || preview.matched.length === 0}
            className="rounded bg-primary px-4 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-50"
          >
            {confirming ? "Tuodaan…" : "Vahvista tuonti"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main panel ─────────────────────────────────────────────────────────────────

export function DataPointMappingPanel({
  buildingId,
  equipmentId,
  operator,
  initial_declared,
  on_declared_changed,
  on_close,
}: Props) {
  const [kb_components, set_kb_components] = useState<KBComponentDef[]>([]);
  const [loading, set_loading] = useState(true);
  const [kb_missing, set_kb_missing] = useState(false);
  const [present_ids, set_present_ids] = useState<Set<string>>(new Set());
  const [legacy_data, set_legacy_data] = useState(false);
  const [last_samples, set_last_samples] = useState<
    Map<string, DataPointSample>
  >(new Map());
  const [declared, set_declared] = useState<Set<string>>(
    new Set(initial_declared),
  );
  const [saving, set_saving] = useState(false);

  const [upload_phase, set_upload_phase] = useState<UploadPhase>("idle");
  const [preview, set_preview] = useState<UploadPreviewOut | null>(null);
  const [rows_written, set_rows_written] = useState<number | null>(null);
  const [upload_error, set_upload_error] = useState<string | null>(null);
  // csv_column -> canonical_id for unmatched columns the operator remapped.
  const [remap_assignments, set_remap_assignments] = useState<
    Record<string, string>
  >({});
  // Null while loading or when the equipment has no KB type — the remap UI
  // degrades to the pre-existing read-only "Ohitetut sarakkeet" list.
  const [candidate_groups, set_candidate_groups] = useState<
    RemapCandidateGroup[] | null
  >(null);
  const file_input_ref = useRef<HTMLInputElement>(null);
  const pending_file_ref = useRef<File | null>(null);
  // Stable ref so refresh_presence never captures a stale kb_dp_ids value.
  const kb_dp_ids_ref = useRef<Set<string>>(new Set());

  function apply_sources(sources: DataPointSource[]) {
    const kb_ids = kb_dp_ids_ref.current;
    const ids = canonical_ids_present(sources, equipmentId, kb_ids);
    set_present_ids(ids);
    set_legacy_data(has_only_legacy_names(sources, equipmentId, kb_ids));
  }

  useEffect(() => {
    void Promise.all([
      getEquipmentKBDefinition(buildingId, equipmentId).catch(() => null),
      listDataPointSources(buildingId).catch((): DataPointSource[] => []),
    ]).then(([kb_def, sources]) => {
      if (kb_def === null) {
        set_kb_missing(true);
      } else {
        set_kb_components(kb_def.components);
        set_candidate_groups(equipmentCandidateGroups(kb_def));
        // Shared data points are canonical ids too. Omitting them made an import of
        // only shared sensors (the four AHU air temperatures) read as legacy data.
        kb_dp_ids_ref.current = new Set([
          ...kb_def.components.flatMap((c) => c.data_points.map((dp) => dp.id)),
          ...(kb_def.shared_data_points ?? []).map((dp) => dp.id),
        ]);
      }
      apply_sources(sources);
      set_loading(false);

      const kb_ids = kb_dp_ids_ref.current;
      const eq_src = sources.find((s) => s.source_id === equipmentId);
      const present_names = (eq_src?.available_names ?? [])
        .map((n) => n.name)
        .filter((n) => kb_ids.has(n));
      if (present_names.length > 0) {
        const from = new Date(Date.now() - 30 * 24 * 3_600_000).toISOString();
        void getDataPointHistory(
          buildingId,
          [equipmentId],
          present_names,
          from,
          1000,
        )
          .then((samples) => {
            const latest = new Map<string, DataPointSample>();
            for (const s of samples) {
              const prev = latest.get(s.name);
              if (!prev || s.recorded_at > prev.recorded_at)
                latest.set(s.name, s);
            }
            set_last_samples(latest);
          })
          .catch(() => {});
      }
    });
    // apply_sources is stable within each render cycle; ref keeps kb_dp_ids current.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId, equipmentId]);

  function refresh_presence() {
    void listDataPointSources(buildingId)
      .catch((): DataPointSource[] => [])
      .then(apply_sources);
  }

  const handle_toggle = useCallback(
    async (dp_id: string) => {
      if (!operator || saving) return;
      const next = new Set(declared);
      if (next.has(dp_id)) next.delete(dp_id);
      else next.add(dp_id);
      set_declared(next);
      set_saving(true);
      try {
        await updateEquipment(buildingId, equipmentId, {
          declared_data_points: Array.from(next),
        });
        on_declared_changed(Array.from(next));
      } catch {
        set_declared(declared);
      } finally {
        set_saving(false);
      }
    },
    [buildingId, equipmentId, operator, saving, declared, on_declared_changed],
  );

  async function handle_file_selected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    pending_file_ref.current = file;
    set_upload_phase("previewing");
    set_upload_error(null);
    set_rows_written(null);
    set_remap_assignments({});
    try {
      const result = await uploadEquipmentDataPreview(
        buildingId,
        equipmentId,
        file,
      );
      set_preview(result);
      set_upload_phase("preview_ready");
    } catch (err) {
      set_upload_error(
        err instanceof Error ? err.message : "Esikatselu epäonnistui",
      );
      set_upload_phase("error");
    }
    if (file_input_ref.current) file_input_ref.current.value = "";
  }

  function handle_assign_unmatched(
    csv_column: string,
    canonical_id: string | null,
  ) {
    set_remap_assignments((prev) => {
      if (canonical_id === null) {
        const next = { ...prev };
        delete next[csv_column];
        return next;
      }
      return { ...prev, [csv_column]: canonical_id };
    });
  }

  async function handle_confirm() {
    if (!preview || !pending_file_ref.current) return;
    const schema: Record<string, string> = {};
    for (const m of preview.matched) {
      schema[m.csv_column] = m.canonical_id;
    }
    // Operator-remapped unmatched columns merge into the same schema built from
    // matched columns (#898) — an assigned unmatched column is never omitted.
    Object.assign(schema, remap_assignments);
    set_upload_phase("confirming");
    set_upload_error(null);
    try {
      const result = await confirmEquipmentDataUpload(
        buildingId,
        equipmentId,
        pending_file_ref.current,
        schema,
      );
      set_rows_written(result.rows_written);
      set_upload_phase("done");
      set_preview(null);
      pending_file_ref.current = null;
      set_remap_assignments({});
      refresh_presence();
    } catch (err) {
      set_upload_error(
        err instanceof Error ? err.message : "Tuonti epäonnistui",
      );
      set_upload_phase("preview_ready");
    }
  }

  function handle_cancel_preview() {
    set_preview(null);
    pending_file_ref.current = null;
    set_upload_phase("idle");
    set_upload_error(null);
    set_remap_assignments({});
  }

  const all_dp_ids = kb_components.flatMap((c) =>
    c.data_points.map((dp) => dp.id),
  );
  const present_count = all_dp_ids.filter((id) => present_ids.has(id)).length;
  const total_count = all_dp_ids.length;

  return (
    <>
      {(upload_phase === "preview_ready" || upload_phase === "confirming") &&
        preview && (
          <UploadPreviewModal
            preview={preview}
            confirming={upload_phase === "confirming"}
            error={upload_error}
            candidate_groups={candidate_groups}
            remap_assignments={remap_assignments}
            on_assign_unmatched={handle_assign_unmatched}
            on_confirm={() => void handle_confirm()}
            on_cancel={handle_cancel_preview}
          />
        )}

      <div className={cn("mt-2", cardSubtle, "p-4")}>
        {/* Header */}
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Tietolähteen kytkentä
            </p>
            {!loading && !kb_missing && (
              <span className="text-[10px] text-muted-foreground">
                {present_count}/{total_count} tuotu · {declared.size} aktivoitu
              </span>
            )}
            {saving && (
              <span className="text-[10px] text-muted-foreground">
                Tallennetaan…
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {operator && (
              <>
                <input
                  ref={file_input_ref}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={(e) => void handle_file_selected(e)}
                />
                <button
                  onClick={() => file_input_ref.current?.click()}
                  disabled={
                    upload_phase === "previewing" ||
                    upload_phase === "confirming"
                  }
                  className="rounded bg-primary/10 px-3 py-1 text-[10px] font-medium text-primary hover:bg-primary/20 disabled:opacity-50"
                >
                  {upload_phase === "previewing"
                    ? "Analysoidaan…"
                    : "Tuo CSV-tiedosto"}
                </button>
              </>
            )}
            <button
              onClick={on_close}
              className="rounded px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground"
            >
              Sulje ✕
            </button>
          </div>
        </div>

        {/* Status banners */}
        {!loading && !kb_missing && legacy_data && (
          <div className="mb-3 rounded-lg border border-warning/20 bg-warning/5 px-3 py-2 text-[10px] text-warning-foreground">
            Laitteelle löytyy historiadataa, mutta se ei käytä KB-tunnisteita.
            Tuo data uudelleen CSV-tiedostolla — tuonti normalisoi sarakkeiden
            nimet automaattisesti.
          </div>
        )}
        {upload_phase === "done" && rows_written !== null && (
          <div className="mb-3 rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3 py-2 text-[10px] text-emerald-400">
            {rows_written} mittauspistettä tuotu onnistuneesti.
          </div>
        )}
        {upload_phase === "error" && upload_error && (
          <div className="mb-3 rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2 text-[10px] text-destructive">
            {upload_error}
          </div>
        )}

        {/* Content */}
        {loading ? (
          <p className="text-xs text-muted-foreground">Ladataan…</p>
        ) : kb_missing ? (
          <p className="text-xs text-muted-foreground italic">
            KB-määritystä ei löydy tälle laitetyypille.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border/30">
                  <th className="pb-1.5 pr-3 text-left font-medium text-muted-foreground">
                    Nimi
                  </th>
                  <th className="pb-1.5 pr-3 text-left font-medium text-muted-foreground">
                    Tärkeys
                  </th>
                  <th className="pb-1.5 pr-3 text-left font-medium text-muted-foreground">
                    Laji
                  </th>
                  <th className="pb-1.5 pr-3 text-left font-medium text-muted-foreground">
                    KB-tunniste
                  </th>
                  <th className="pb-1.5 pr-3 text-left font-medium text-muted-foreground">
                    Viimeisin arvo
                  </th>
                  <th
                    className="pb-1.5 pl-2 text-left font-medium text-muted-foreground"
                    title="Aktivoi signaalit, joita tämä laite tuottaa"
                  >
                    Aktiivinen
                  </th>
                </tr>
              </thead>
              <tbody>
                {kb_components.map((component) => {
                  const sorted_dps = [...component.data_points].sort(
                    (a, b) =>
                      (a.order ?? 9999) - (b.order ?? 9999) ||
                      (KIND_ORDER[a.kind] ?? 9) - (KIND_ORDER[b.kind] ?? 9),
                  );
                  const local_dps = sorted_dps.filter(
                    (dp) => !dp.connection_source,
                  );
                  const linked_dps = sorted_dps.filter(
                    (dp) => !!dp.connection_source,
                  );
                  return (
                    <Fragment key={component.id}>
                      <tr>
                        <td
                          colSpan={6}
                          className="pb-0.5 pt-3 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60"
                        >
                          {component.name_fi}
                          {component.optional && (
                            <span className="ml-1.5 font-normal normal-case tracking-normal text-muted-foreground/40">
                              (valinnainen)
                            </span>
                          )}
                        </td>
                      </tr>
                      {local_dps.map((dp) => (
                        <DataPointRow
                          key={dp.id}
                          component_id={component.id}
                          dp={dp}
                          present={present_ids.has(dp.id)}
                          declared={declared.has(dp.id)}
                          operator={operator}
                          on_toggle={(id) => void handle_toggle(id)}
                          last_sample={last_samples.get(dp.id)}
                        />
                      ))}
                      {linked_dps.length > 0 && (
                        <>
                          <tr>
                            <td
                              colSpan={6}
                              className="pb-0.5 pt-2 text-[9px] font-semibold uppercase tracking-widest text-muted-foreground/40"
                            >
                              Kytkentäsyötteet
                            </td>
                          </tr>
                          {linked_dps.map((dp) => (
                            <tr
                              key={dp.id}
                              className="border-b border-border/20 opacity-60"
                            >
                              <td className="py-2 pr-3">
                                <span className="text-xs font-medium">
                                  {dp.name_fi}
                                </span>
                                {dp.unit && (
                                  <span className="ml-1 text-[10px] text-muted-foreground">
                                    {dp.unit}
                                  </span>
                                )}
                                <span className="ml-2 text-[10px] text-muted-foreground/50">
                                  ← {dp.connection_source}
                                </span>
                              </td>
                              <td className="py-2 pr-3">
                                <span
                                  className={cn(
                                    "rounded px-1.5 py-0.5 text-[10px] font-medium",
                                    CRITICALITY_CLASS[dp.criticality] ??
                                      "bg-muted text-muted-foreground",
                                  )}
                                >
                                  {CRITICALITY_LABEL[dp.criticality] ??
                                    dp.criticality}
                                </span>
                              </td>
                              <td className="py-2 pr-3">
                                {dp.kind && (
                                  <span
                                    className={cn(
                                      "rounded px-1.5 py-0.5 text-[10px] font-medium",
                                      KIND_CLASS[dp.kind] ??
                                        "bg-muted text-muted-foreground",
                                    )}
                                  >
                                    {KIND_LABEL[dp.kind] ?? dp.kind}
                                  </span>
                                )}
                              </td>
                              <td className="py-2 pr-3 font-mono text-[10px] text-muted-foreground/60">
                                {component.id}.{dp.id}
                              </td>
                              <td className="py-2 pr-3">
                                {last_samples.get(dp.id) ? (
                                  <span className="text-xs font-medium tabular-nums">
                                    {format_sample(
                                      last_samples.get(dp.id)!,
                                      dp.unit,
                                    )}
                                  </span>
                                ) : (
                                  <span className="text-[10px] text-muted-foreground/40">
                                    — Ei dataa
                                  </span>
                                )}
                              </td>
                              <td className="py-2 pl-2" />
                            </tr>
                          ))}
                        </>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
